import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Card from "@mui/joy/Card";
import CardContent from "@mui/joy/CardContent";
import Typography from "@mui/joy/Typography";
import { styled } from "@mui/joy/styles";
import React from "react";

const DropZone = styled(Card)(({ theme }) => ({
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: theme.radius.md,
  padding: theme.spacing(4),
  textAlign: "center",
  cursor: "pointer",
  transition: "all 0.2s ease-in-out",
  minHeight: "200px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&:hover": {
    borderColor: theme.palette.primary[500],
    backgroundColor: theme.palette.primary[50],
  },
  "&.dragover": {
    borderColor: theme.palette.primary[500],
    backgroundColor: theme.palette.primary[100],
  },
}));

const HiddenInput = styled("input")({
  display: "none",
});

interface DocumentUploadAreaProps {
  onUpload: (files: File[]) => void;
  disabled?: boolean;
}

export const DocumentUploadArea: React.FC<DocumentUploadAreaProps> = ({
  onUpload,
  disabled = false,
}) => {
  const [isDragOver, setIsDragOver] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      onUpload(files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      onUpload(files);
    }
    // Reset the input value so the same file can be selected again
    e.target.value = "";
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <DropZone
      className={isDragOver ? "dragover" : ""}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
      sx={{
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      <HiddenInput
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.doc,.docx,.txt"
        onChange={handleFileSelect}
        disabled={disabled}
      />
      <CardContent sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <CloudUploadIcon
          sx={{
            fontSize: "3rem",
            color: isDragOver ? "primary.500" : "text.secondary",
            mb: 2,
          }}
        />
        <Typography level="h4" sx={{ mb: 1, textAlign: "center" }}>
          {isDragOver ? "Drop files here" : "Drag & drop documents"}
        </Typography>
        <Typography level="body-md" color="neutral" sx={{ textAlign: "center" }}>
          or click to browse your files
        </Typography>
        <Typography level="body-sm" color="neutral" sx={{ mt: 1, textAlign: "center" }}>
          Supported formats: PDF, DOC, DOCX, TXT
        </Typography>
      </CardContent>
    </DropZone>
  );
};
